import {
  Button,
  Modal,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  ModalFooter,
  ModalBody,
  Text,
  useColorMode,
  useColorStyle,
  Link,
  Stack,
  Space,
} from '@tonic-one/react';
import { useToggle } from '@tonic-one/react-hooks';
import { useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Steps, StepsProps } from 'antd';
import { ProvisionOrgInfo, StepIcon } from '@Dashboard/ProvisionModal/share';
import { deleteCookie, getProvisionCookie } from '@Utils/common';
import { ProvisionApi } from '@Apis';
import { EnabledPolicyTypeEnum } from '@Types/provision';
import CSpinner from '@Components/CSpinner';
import ErrorAlert from '@Components/Common/ErrorAlert';
import { THIRD_PARTY_LINK } from '@Utils/casDocLink';

import { Action } from './type';
import { AUDIT_LOG_BANNER_EVENT_NAME } from './const';
import { PRODUCT_NAME } from '../../../utils/const';
import { ExternalLinkIcon } from '@tonic-one/react-icons';
import { App } from '@Types/app';

enum ProvisionStatus {
  NotStart = 0,
  Done = 1,
}

const COOKIE_NAME = 'provision_status_microsoft_audit_log';
const SERVICE_NAME = App.O365AuditLog;

export const MSAuditLogProvisionModal: React.FC<{
  isOpen: boolean;
  onModalClose: () => void;
  authUrl: string;
  action: Action;
  orgId: string;
}> = ({ authUrl, isOpen, onModalClose, orgId }) => {
  const { t } = useTranslation(['dashboard', 'common', 'provision']);
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const [errorMsg, setErrorMsg] = useState<string | undefined>(undefined);

  const [provisionStatus, setProvisionStatus] = useState<ProvisionStatus>(
    ProvisionStatus.NotStart
  );

  const items: StepsProps['items'] = [
    {
      title: t('dashboard.msAuditLog.modal.desc.step1'),
      description: (
        <Stack alignItems="flex-start" gap="8px" my="8px">
          {provisionStatus === ProvisionStatus.NotStart ? (
            <GrantPermission
              {...{ authUrl, setProvisionStatus, orgId, setErrorMsg }}
            />
          ) : (
            <Text color={colorStyle.color.tertiary} lineHeight="20px">
              {t('provision:provision.common.permissionGranted')}
            </Text>
          )}
          <Text color={colorStyle.color.tertiary}>
            <Trans
              ns="dashboard"
              i18nKey="dashboard.msAuditLog.modal.desc.turnOn"
              values={{ productName: PRODUCT_NAME }}
            >
              placeholder
              <LearnMoreLink>placeholder</LearnMoreLink>
            </Trans>
          </Text>
        </Stack>
      ),
      icon: <StepIcon checked={provisionStatus > ProvisionStatus.NotStart} />,
    },
  ];

  const onClose = () => {
    // if provision success, hidden the banner
    if (provisionStatus === ProvisionStatus.Done) {
      window.dispatchEvent(new CustomEvent(AUDIT_LOG_BANNER_EVENT_NAME));
    }
    onModalClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} closeOnEsc isClosable size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t('dashboard.msAuditLog.modal.header')}</ModalHeader>
        <ModalBody>
          <ProvisionOrgInfo orgId={orgId} />
          {errorMsg && (
            <ErrorAlert
              errorMsg={errorMsg}
              onClose={() => setErrorMsg(undefined)}
            />
          )}

          <Stack gap="8px">
            <Text>{t('dashboard.msAuditLog.modal.desc.grant')}</Text>
            <Text color={colorStyle.color.secondary}>
              {t('dashboard.msAuditLog.modal.desc.requires')}
            </Text>
            <Space height="4px" />
            <Steps direction="vertical" items={items} />
            <Text color={colorStyle.color.secondary}>
              {t('dashboard.msAuditLog.modal.desc.notify')}
            </Text>
          </Stack>
        </ModalBody>
        <ModalFooter>
          <Button onClick={onClose} variant="default">
            {t('common:common.action.label.close')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

const GrantPermission = ({
  authUrl,
  setErrorMsg,
  orgId,
  setProvisionStatus,
}: {
  authUrl: string;
  setErrorMsg: (s?: string) => void;
  orgId: string;
  setProvisionStatus: (v: ProvisionStatus) => void;
}) => {
  const { t } = useTranslation('provision');
  const [loading, toggleLoading] = useToggle(false);
  const timeRef = useRef<any>(null);
  const onClick = async () => {
    if (!authUrl) {
      return;
    }
    setErrorMsg(undefined);
    toggleLoading(true);
    window.open(authUrl, '_blank');
    if (timeRef.current) {
      clearInterval(timeRef.current);
    }
    timeRef.current = setInterval(async () => {
      const provisionInfo = getProvisionCookie(COOKIE_NAME);
      if (provisionInfo) {
        clearInterval(timeRef.current);
        deleteCookie('provision_status_');
        const { error, code } = JSON.parse(provisionInfo);
        if (error === undefined || error.length === 0) {
          try {
            const res = await ProvisionApi.apiSaveProvisionInfo(
              SERVICE_NAME,
              code,
              EnabledPolicyTypeEnum.None,
              orgId
            );
            if (res.data?.status === 'failure') {
              setErrorMsg(
                t([
                  `provision:provision.service.exchange.error.saveInfo.${res.data?.reason}`,
                  'common:common.apiException.internalError',
                ])
              );
            } else {
              setProvisionStatus(ProvisionStatus.Done);
            }
          } catch {
            setErrorMsg(t('common:common.apiException.internalError'));
          }
        } else {
          setErrorMsg(
            t('provision:provision.service.exchange.error.deniedGrant')
          );
        }
        toggleLoading(false);
      }
    }, 1000);
  };
  useEffect(() => {
    if (timeRef.current) {
      clearInterval(timeRef.current);
    }
    return () => {
      if (timeRef.current) {
        clearInterval(timeRef.current);
      }
    };
  }, []);
  return (
    <>
      {loading && <CSpinner />}
      <Button onClick={onClick} variant="secondary" borderRadius="2rem">
        {t('provision:provision.button.grantPermission')}
      </Button>
    </>
  );
};

const LearnMoreLink = ({ children }: { children: string }) => (
  <Link href={THIRD_PARTY_LINK.o365AuditLogTurnOn} target="_blank">
    {children}
    <ExternalLinkIcon size="12px" alignSelf="center" />
  </Link>
);
